import Phaser from 'phaser';
import { DESIGN_HEIGHT, DESIGN_WIDTH, LAYOUT } from '../PhaserGame.js';
import { navigateTo } from '../sceneNav.js';
import { NavBar } from './NavBar.js';

/**
 * Preload the bottom-nav PNG icons. Call from every scene's preload()
 * that mounts the nav — Phaser deduplicates by texture key so the
 * loader skips already-cached entries on subsequent scene boots.
 *
 * All five slots now ship as white-on-transparent PNGs, recolored at
 * runtime via setTint. The original vector draw fns in NavBar.ts are
 * unused at this call site but remain exported in case any external
 * caller ever wants them back.
 */
export function preloadNavAssets(scene: Phaser.Scene): void {
  scene.load.image('nav-home', '/assets/nav-home.png');
  scene.load.image('nav-games', '/assets/nav-games.png');
  scene.load.image('nav-friends', '/assets/nav-friends.png');
  scene.load.image('nav-trophy', '/assets/nav-trophy.png');
  scene.load.image('nav-settings', '/assets/nav-settings.png');
}

/**
 * Shared mounting helper for the app's bottom navigation bar.
 *
 * The five-icon strip (home / games / chat / leaderboards / settings)
 * lives on every "shell" scene — Home, Inbox, Settings, HowTo, About,
 * Game — so the chrome pattern reads as one app instead of a stack
 * of unrelated pages.
 *
 * Routing:
 *   - `home`         → HomeScene
 *   - `games`        → InboxScene
 *   - `friends`      → FriendsScene (v1.0 Phase B Slice 1)
 *   - `leaderboards` → console-log placeholder until that screen
 *                      gets built (v1.0+ work)
 *   - `settings`     → SettingsScene (the consolidated Settings/HowTo/
 *                      About group; tabs in-scene switch between them)
 *
 * The `friends` slot used to be `chat` in the pre-v1.0 chrome. The
 * chat-bubble icon + handler are kept in NavBar.ts (drawChatIcon) so
 * a future chat feature can drop straight back in if/when it ships.
 *
 * Tapping the active tab is normally a no-op. Scenes that are
 * sub-screens of a section (e.g. a specific game on the games tab,
 * a specific lobby on JoinScene) pass `allowActiveTap: true` so
 * re-tapping the highlighted icon routes back to that section's
 * canonical scene (Inbox for games).
 *
 * Scenes that need to intercept a tap (e.g. confirm-before-leave
 * for an in-progress local SP game) pass `onIntercept`. The callback
 * receives the tapped id and a `defaultRoute` thunk; call the thunk
 * to allow the navigation, or skip calling it to block.
 *
 * Returns the NavBar instance in case the caller wants to flip the
 * active slot programmatically (currently no scene does).
 */
export type AppNavId =
  | 'home'
  | 'games'
  | 'friends'
  | 'leaderboards'
  | 'settings';

export interface AppNavOptions {
  /** When true, tapping the already-active tab routes to that
   *  section's canonical scene instead of being a no-op. Used by
   *  JoinScene + GameScene since they're sub-screens of "games". */
  readonly allowActiveTap?: boolean;
  /** Optional gate. The callback fires on every tap (including the
   *  active-tab case when `allowActiveTap` is true); call
   *  `defaultRoute()` to allow navigation, skip it to block. */
  readonly onIntercept?: (
    tappedId: AppNavId,
    defaultRoute: () => void
  ) => void;
}

// All five slots ship as PNG icons (white-on-transparent, tinted at
// runtime). NavBar still supports mixed Graphics+Image items via its
// discriminated-union NavBarItem in case a future slot wants to drop
// back to a vector draw fn.
const APP_NAV_ITEMS = [
  { id: 'home', imageKey: 'nav-home' },
  { id: 'games', imageKey: 'nav-games' },
  { id: 'friends', imageKey: 'nav-friends' },
  { id: 'leaderboards', imageKey: 'nav-trophy' },
  { id: 'settings', imageKey: 'nav-settings' },
] as const;

const ID_TO_INDEX: Readonly<Record<AppNavId, number>> = {
  home: 0,
  games: 1,
  friends: 2,
  leaderboards: 3,
  settings: 4,
};

export function attachBottomNav(
  scene: Phaser.Scene,
  activeId: AppNavId,
  options: AppNavOptions = {}
): NavBar {
  const navY = DESIGN_HEIGHT - LAYOUT.bottomSafe - LAYOUT.navHeight;
  const activeIndex = ID_TO_INDEX[activeId];

  const bar = new NavBar(
    scene,
    0,
    navY,
    DESIGN_WIDTH,
    LAYOUT.navHeight,
    APP_NAV_ITEMS,
    activeIndex,
    (id) => {
      const tappedId = id as AppNavId;
      const defaultRoute = () => routeNavSelect(scene, tappedId, activeId);
      if (options.onIntercept) {
        options.onIntercept(tappedId, defaultRoute);
      } else {
        defaultRoute();
      }
    },
    options.allowActiveTap === true
  );

  return bar;
}

function routeNavSelect(
  scene: Phaser.Scene,
  tappedId: AppNavId,
  activeId: AppNavId
): void {
  // Active-tap path: route back to the section's canonical scene
  // WITHOUT pushing onto the history stack — otherwise Back from the
  // canonical scene would just pop you back into the sub-screen you
  // tapped away from. Non-active taps use navigateTo so Back works.
  const isActiveTap = tappedId === activeId;
  switch (tappedId) {
    case 'home':
      if (isActiveTap) scene.scene.start('Home');
      else navigateTo(scene, 'Home');
      return;
    case 'games':
      if (isActiveTap) scene.scene.start('Inbox');
      else navigateTo(scene, 'Inbox');
      return;
    case 'friends':
      if (isActiveTap) scene.scene.start('Friends');
      else navigateTo(scene, 'Friends');
      return;
    case 'settings':
      // Lands on SettingsScene — the user can switch to HOW TO PLAY
      // or INFO via the in-scene SectionTabs once they're there.
      if (isActiveTap) scene.scene.start('Settings');
      else navigateTo(scene, 'Settings');
      return;
    case 'leaderboards':
      // Placeholder until that screen gets built (v1.0+ work).
      // Console log so a tester knows the tap registered even
      // though nothing visible changes.
      console.log(`[appnav] ${tappedId} screen not built yet`);
      return;
  }
}

/**
 * Y-coordinate of the top edge of the bottom nav bar. Useful for
 * sizing scroll masks so content doesn't bleed under the nav.
 */
export function bottomNavTopY(): number {
  return DESIGN_HEIGHT - LAYOUT.bottomSafe - LAYOUT.navHeight;
}
